import { useMemo, useEffect, memo } from 'react';

import { checkKkb, parseKkb, TStrKeyBind } from './keyUtils';

type THandler =
  | { onKeyDown: (event: TAnyEvent) => unknown }
  | { onKeyPress: (event: TAnyEvent) => unknown }
  | { onKeyUp: (event: TAnyEvent) => unknown };

type TProps = THandler & {
  kkb: TStrKeyBind | TStrKeyBind[];
  domNode?: HTMLElement | Document;
  disabled?: boolean;
  checkInControls?: boolean;
};

const focusInControl = (): boolean =>
  ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName || '');

const KeyBind: React.FC<TProps> = ({
  kkb: template,
  domNode = window.document,
  disabled,
  checkInControls = false,
  ...handlers
}) => {
  const kkbs = useMemo(
    () =>
      (typeof template === 'string' ? [template] : template).map(template =>
        parseKkb(template)
      ),
    [template]
  );

  useEffect(() => {
    if (!domNode) return;

    const eventConfig =
      'onKeyPress' in handlers
        ? { handler: handlers.onKeyPress, type: 'keypress' }
        : 'onKeyUp' in handlers
          ? { handler: handlers.onKeyUp, type: 'keyup' }
          : { handler: handlers.onKeyDown, type: 'keydown' };

    const handler = ((event: KeyboardEvent) => {
      if (!checkInControls && focusInControl()) return;

      if (!disabled && kkbs.some(kkb => checkKkb(event, kkb))) {
        eventConfig.handler(event);
      }
    }) as EventListener;

    domNode.addEventListener(eventConfig.type, handler);
    return () => domNode.removeEventListener(eventConfig.type, handler);
  }, [disabled, domNode, kkbs, checkInControls, ...Object.values(handlers)]);

  return null;
};

export default memo(KeyBind);
